import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SettingsIcon, GitMergeIcon, WorkflowIcon, DatabaseIcon, MessageSquareIcon, FileTextIcon, CheckCircleIcon, ArrowRightIcon, SearchIcon, MapIcon, WrenchIcon, ActivityIcon, RocketIcon, LayersIcon, CpuIcon, AlertCircleIcon, ChevronDownIcon, ZapIcon, BuildingIcon, UsersIcon } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { ScrollReveal } from '../components/ScrollReveal';
export function CustomAutomationsPage() {
  const faqs = [{
    question: 'What kind of automations can you build for contractors?',
    answer: 'We build bespoke workflows to eliminate manual data entry. Common examples include syncing leads from ad platforms directly into your dispatch software, automatically sending estimate follow-ups, triggering review requests when a job is marked complete, and routing specific job types to specific crews.'
  }, {
    question: 'How long does a custom automation project take?',
    answer: 'Simple automations (like connecting a web form to a Google Sheet) can be done in a few days. Complex, multi-step workflows involving custom API integrations and conditional logic typically take 2-4 weeks to build, test, and deploy.'
  }, {
    question: 'What tools and platforms can you integrate?',
    answer: 'We can integrate with almost any software that has an open API. We frequently work with ServiceTitan, Jobber, Housecall Pro, GoHighLevel, Salesforce, HubSpot, QuickBooks, Stripe, Google Workspace, and major ad platforms.'
  }, {
    question: 'Do I need to know how to code?',
    answer: 'No. We handle all the technical architecture, coding, and API connections. We deliver a finished, working system and provide you with a simple dashboard to monitor it, along with training for your team.'
  }, {
    question: 'How much does a custom automation cost?',
    answer: 'Pricing depends entirely on the complexity of the workflow and the systems involved. We start with a free scoping call to understand your operational bottlenecks, then provide a flat-rate project fee.'
  }, {
    question: 'Can you automate workflows I already have in place?',
    answer: 'Yes. We often audit existing, manual workflows (like office staff copy-pasting data between spreadsheets) and build automated systems to replace them, saving hours of administrative work each week and eliminating human error.'
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-1622">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-1623">
          <span className="text-lg font-bold text-white group-hover:text-red-500 transition-colors" data-id="element-1624">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-red-500' : ''}`} data-id="element-1625" />
        </button>
        <AnimatePresence data-id="element-1626">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-1627">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1628">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="pt-24 pb-16" data-id="element-1629">
      <SEOHead title="Custom Automations & API Integrations | Viridia Analytics" description="Bespoke automation workflows for home service businesses. Connect your tools, eliminate manual data entry, and build systems tailored to your unique operations." canonical="/custom-automations" keywords="custom automations, API integrations, workflow automation, business process automation, Zapier alternative, custom webhooks, Phoenix automation agency, marketing agency phoenix, HVAC, painters, landscapers, contractors, window installers" faq={faqs} data-id="element-1630" />

      {/* Hero Section */}
      <section className="relative pt-12 pb-20 overflow-hidden" data-id="element-1631">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-1632">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-red-500/10 blur-[120px] animate-pulse-slow" data-id="element-1633"></div>
        </div>

        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTTAgNDBoNDBWMEgwem0zOS0zOXYzOEgxVjFoMzh6IiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDIpIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] -z-10 opacity-50" data-id="element-1634"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-1635">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1636">
            <ScrollReveal data-id="element-1637">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 text-sm font-medium mb-6" data-id="element-1638">
                <SettingsIcon className="w-4 h-4" data-id="element-1639" />
                <span data-id="element-1640">Bespoke Systems Engineering</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-id="element-1641">
                Connect Your Tools. <br data-id="element-1642" />
                <span className="text-red-500" data-id="element-1643">Automate the Rest.</span>
              </h1>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl" data-id="element-1644">
                When off-the-shelf software doesn't fit your unique process, we
                build custom workflows that make your existing tools talk to
                each other. Eliminate manual data entry and operational
                bottlenecks.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1645">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-1646">
                  Book a Scoping Call
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="hidden lg:block" data-id="element-1647">
              <div className="relative" data-id="element-1648">
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 relative overflow-hidden" data-id="element-1649">
                  {/* Workflow Diagram Visualization */}
                  <div className="flex flex-col items-center gap-4 relative z-10 py-4" data-id="element-1650">
                    {/* Trigger */}
                    <motion.div initial={{
                    opacity: 0,
                    y: -20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.5
                  }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full max-w-64 shadow-lg" data-id="element-1651">
                      <div className="w-8 h-8 rounded bg-blue-500/20 flex items-center justify-center flex-shrink-0" data-id="element-1652">
                        <FileTextIcon className="w-4 h-4 text-blue-400" data-id="element-1653" />
                      </div>
                      <div className="min-w-0" data-id="element-1654">
                        <div className="text-xs text-gray-500" data-id="element-1655">Trigger</div>
                        <div className="text-sm font-medium text-white truncate" data-id="element-1656">
                          New Quote Form Submitted
                        </div>
                      </div>
                    </motion.div>

                    <div className="w-px h-8 bg-gray-700" data-id="element-1657"></div>

                    {/* Logic Branch */}
                    <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.5,
                    delay: 0.3
                  }} className="bg-viridia-black border border-viridia/30 rounded-lg p-3 flex items-center gap-3 w-full max-w-64 shadow-lg relative" data-id="element-1658">
                      <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-px bg-viridia/50" data-id="element-1659"></div>
                      <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-px bg-viridia/50" data-id="element-1660"></div>

                      <div className="w-8 h-8 rounded bg-viridia/20 flex items-center justify-center" data-id="element-1661">
                        <CpuIcon className="w-4 h-4 text-viridia" data-id="element-1662" />
                      </div>
                      <div data-id="element-1663">
                        <div className="text-xs text-gray-500" data-id="element-1664">Action</div>
                        <div className="text-sm font-medium text-white" data-id="element-1665">
                          AI Summarizes Request
                        </div>
                      </div>
                    </motion.div>

                    <div className="flex w-full max-w-md justify-between px-12 relative" data-id="element-1666">
                      <div className="w-px h-8 bg-gray-700 absolute left-1/4 top-0" data-id="element-1667"></div>
                      <div className="w-px h-8 bg-gray-700 absolute right-1/4 top-0" data-id="element-1668"></div>
                      <div className="w-1/2 h-px bg-gray-700 absolute left-1/4 top-0" data-id="element-1669"></div>
                    </div>

                    {/* Outcomes */}
                    <div className="flex gap-4 sm:gap-8 w-full justify-center flex-wrap sm:flex-nowrap" data-id="element-1670">
                      <motion.div initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.6
                    }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full sm:w-48 shadow-lg" data-id="element-1671">
                        <div className="w-8 h-8 rounded bg-green-500/20 flex items-center justify-center" data-id="element-1672">
                          <DatabaseIcon className="w-4 h-4 text-green-400" data-id="element-1673" />
                        </div>
                        <div data-id="element-1674">
                          <div className="text-xs text-gray-500" data-id="element-1675">CRM</div>
                          <div className="text-sm font-medium text-white" data-id="element-1676">
                            Create Record
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      x: 20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.8
                    }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full sm:w-48 shadow-lg" data-id="element-1677">
                        <div className="w-8 h-8 rounded bg-purple-500/20 flex items-center justify-center flex-shrink-0" data-id="element-1678">
                          <MessageSquareIcon className="w-4 h-4 text-purple-400" data-id="element-1679" />
                        </div>
                        <div className="min-w-0" data-id="element-1680">
                          <div className="text-xs text-gray-500" data-id="element-1681">Slack</div>
                          <div className="text-sm font-medium text-white truncate" data-id="element-1682">
                            Alert Sales Team
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Animated Pulses */}
                    <motion.div animate={{
                    top: ['10%', '90%'],
                    opacity: [0, 1, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-viridia rounded-full shadow-[0_0_10px_rgba(16,185,129,0.8)] z-20" data-id="element-1683"></motion.div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Why Custom Automations? */}
      <section className="py-20 bg-viridia-black" data-id="element-1684">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1685">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-1686">
            <ScrollReveal data-id="element-1687">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1688">
                Off-the-Shelf Software Rarely Fits Perfectly
              </h2>
              <p className="text-lg text-gray-400 mb-6 leading-relaxed" data-id="element-1689">
                Most local businesses use 5-10 different software tools to run
                their operations. When these tools don't talk to each other,
                your team ends up doing manual data entry, leads fall through
                the cracks, and scaling becomes impossible.
              </p>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1690">
                We build the "glue" that connects your systems. Whether it's a
                simple Zapier integration or a complex custom webhook, we
                engineer workflows that match exactly how you do business.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1691">
                <div className="bg-viridia-surface border border-gray-800 rounded-xl p-4 flex items-start gap-3" data-id="element-1692">
                  <AlertCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-1693" />
                  <div data-id="element-1694">
                    <h4 className="text-white font-bold text-sm mb-1" data-id="element-1695">
                      Without Automation
                    </h4>
                    <p className="text-gray-500 text-xs" data-id="element-1696">
                      Manual data entry, delayed follow-ups, human error.
                    </p>
                  </div>
                </div>
                <div className="bg-viridia-surface border border-viridia/30 rounded-xl p-4 flex items-start gap-3" data-id="element-1697">
                  <ZapIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-1698" />
                  <div data-id="element-1699">
                    <h4 className="text-white font-bold text-sm mb-1" data-id="element-1700">
                      With Viridia
                    </h4>
                    <p className="text-gray-500 text-xs" data-id="element-1701">
                      Instant data sync, automated tasks, scalable operations.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <div className="relative" data-id="element-1702">
              <div className="absolute inset-0 bg-viridia/20 blur-[100px] rounded-full" data-id="element-1703"></div>
              <div className="relative bg-viridia-surface border border-gray-800 rounded-2xl p-8 shadow-2xl" data-id="element-1704">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-1705">
                  <WrenchIcon className="w-5 h-5 text-viridia" data-id="element-1706" /> Our Approach
                </h3>
                <ul className="space-y-6" data-id="element-1707">
                  <li className="flex items-start gap-4" data-id="element-1708">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1709">
                      1
                    </div>
                    <div data-id="element-1710">
                      <h4 className="text-white font-bold mb-1" data-id="element-1711">
                        Process Audit
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1712">
                        We map out your current manual process and identify the
                        bottlenecks costing you time.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1713">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1714">
                      2
                    </div>
                    <div data-id="element-1715">
                      <h4 className="text-white font-bold mb-1" data-id="element-1716">
                        Architecture Design
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1717">
                        We design a robust, scalable system architecture using
                        the right tools (APIs, Webhooks, Zapier, Make).
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1718">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1719">
                      3
                    </div>
                    <div data-id="element-1720">
                      <h4 className="text-white font-bold mb-1" data-id="element-1721">
                        Build & Test
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1722">
                        We build the automation, run extensive edge-case
                        testing, and ensure data integrity.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1723">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1724">
                      4
                    </div>
                    <div data-id="element-1725">
                      <h4 className="text-white font-bold mb-1" data-id="element-1726">
                        Deployment & Monitoring
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1727">
                        We launch the system and provide ongoing monitoring to
                        ensure it runs flawlessly 24/7.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When Custom Makes Sense */}
      <section className="py-20 bg-viridia-surface" data-id="element-1728">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1729">
          <ScrollReveal data-id="element-1730">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1731">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1732">
                When Do You Need Custom Automations?
              </h2>
              <p className="text-gray-400" data-id="element-1733">
                Our standard packages cover 90% of local business needs. Custom
                automations are for the 10% that makes your business unique.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1734">
            <ScrollReveal data-id="element-1735">
              <div className="space-y-6" data-id="element-1736">
                <div className="flex items-start gap-4" data-id="element-1737">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1738">
                    <LayersIcon className="w-5 h-5 text-viridia" data-id="element-1739" />
                  </div>
                  <div data-id="element-1740">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1741">
                      Disconnected Software Stack
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1742">
                      Your field service software (like ServiceTitan or Jobber)
                      doesn't talk to your CRM or accounting software, forcing
                      you to enter data twice.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1743">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1744">
                    <AlertCircleIcon className="w-5 h-5 text-viridia" data-id="element-1745" />
                  </div>
                  <div data-id="element-1746">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1747">
                      Complex Approval Processes
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1748">
                      Quotes over a certain amount need manager approval, or
                      specific job types need to be routed to specific
                      technicians based on logic.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1749">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1750">
                    <DatabaseIcon className="w-5 h-5 text-viridia" data-id="element-1751" />
                  </div>
                  <div data-id="element-1752">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1753">
                      Heavy Admin Burden
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1754">
                      Your team spends hours every week copy-pasting information
                      from emails into spreadsheets or generating manual
                      reports.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1755">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-1756">
                <h3 className="text-xl font-bold text-white mb-6 text-center" data-id="element-1757">
                  The Cost of Manual Work
                </h3>
                <div className="space-y-6" data-id="element-1758">
                  <div data-id="element-1759">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1760">
                      <span className="text-gray-400" data-id="element-1761">Manual Data Entry</span>
                      <span className="text-red-400" data-id="element-1762">High Error Rate</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1763">
                      <div className="bg-red-500 h-2 rounded-full" style={{
                      width: '85%'
                    }} data-id="element-1764"></div>
                    </div>
                  </div>
                  <div data-id="element-1765">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1766">
                      <span className="text-gray-400" data-id="element-1767">Information Silos</span>
                      <span className="text-yellow-400" data-id="element-1768">Slow Handoffs</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1769">
                      <div className="bg-yellow-500 h-2 rounded-full" style={{
                      width: '70%'
                    }} data-id="element-1770"></div>
                    </div>
                  </div>
                  <div className="pt-6 border-t border-gray-800" data-id="element-1771">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1772">
                      <span className="text-white font-medium" data-id="element-1773">
                        Automated System
                      </span>
                      <span className="text-viridia font-medium" data-id="element-1774">
                        Instant & Accurate
                      </span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1775">
                      <div className="bg-viridia h-2 rounded-full" style={{
                      width: '100%'
                    }} data-id="element-1776"></div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <section className="py-20 bg-viridia-surface" data-id="element-1777">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1778">
          <ScrollReveal data-id="element-1779">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1780">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1781">
                Examples of Custom Workflows
              </h2>
              <p className="text-gray-400" data-id="element-1782">
                If it has an API, we can connect it. Here are a few examples of
                systems we can build.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1783">
            {[{
            title: 'Intelligent Lead Routing',
            desc: 'Web form submissions are analyzed by AI. High-value commercial leads are texted directly to the owner, while residential leads go to the standard sales queue.',
            icon: GitMergeIcon
          }, {
            title: 'Cross-Platform Syncing',
            desc: 'When a job is marked "Complete" in Jobber, automatically generate an invoice in QuickBooks and send a review request via Viridia CRM.',
            icon: WorkflowIcon
          }, {
            title: 'AI Call Summaries to Slack',
            desc: "After every recorded sales call, AI generates a 3-bullet summary of the customer's needs and posts it to a Slack channel for the fulfillment team.",
            icon: MessageSquareIcon
          }, {
            title: 'Automated Reporting',
            desc: 'Pull data from Google Ads, Facebook Ads, and your CRM into a custom Google Sheet that updates daily without manual export/import.',
            icon: FileTextIcon
          }, {
            title: 'Inventory Alerts',
            desc: 'When a specific service is booked, automatically check inventory levels in your management software and alert the purchasing manager if parts are needed.',
            icon: AlertCircleIcon
          }, {
            title: 'Custom Webhooks',
            desc: 'Need your proprietary internal software to trigger actions in modern marketing tools? We build the middleware to make it happen.',
            icon: SettingsIcon
          }].map((example, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-1784">
                <div className="bg-viridia-black border border-gray-800 rounded-xl p-6 h-full hover:border-viridia/30 transition-colors" data-id="element-1785">
                  <div className="w-10 h-10 rounded bg-viridia-surface border border-gray-700 flex items-center justify-center mb-4" data-id="element-1786">
                    <example.icon className="w-5 h-5 text-viridia" data-id="element-1787" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-1788">
                    {example.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-1789">
                    {example.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-1790">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1791">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1792">
            <ScrollReveal data-id="element-1793">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1794">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-1795">
                Our custom automation services are designed for businesses with
                established operations that need to scale without adding more
                administrative headcount.
              </p>
              <ul className="space-y-4" data-id="element-1796">
                <li className="flex items-start gap-3" data-id="element-1797">
                  <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-1798" />
                  <div data-id="element-1799">
                    <h4 className="font-bold text-white" data-id="element-1800">
                      Growing Contractors
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-1801">
                      HVAC, painters, landscapers, window installers, and other
                      contractors that have outgrown manual processes and need
                      their software stack to work together seamlessly.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-1802">
                  <UsersIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-1803" />
                  <div data-id="element-1804">
                    <h4 className="font-bold text-white" data-id="element-1805">
                      Teams with Admin Bottlenecks
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-1806">
                      If your office staff spends hours copying data between
                      systems, you need custom automation.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-1807">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-1808">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-1809">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-1810">
                  <Link to="/ai-lead-conversion" className="block p-4 bg-viridia-black rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-1811">
                    <div className="font-bold text-white mb-1" data-id="element-1812">
                      AI Lead Conversion
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-1813">
                      Our flagship system for instant lead response and booking.
                    </div>
                  </Link>
                  <Link to="/crm-business-tools" className="block p-4 bg-viridia-black rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-1814">
                    <div className="font-bold text-white mb-1" data-id="element-1815">
                      CRM & Business Tools
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-1816">
                      Centralize your operations with our robust CRM platform.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1817">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-1818">
          <ScrollReveal data-id="element-1819">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1820">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1821">
              Everything you need to know about our custom automation services.
            </p>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1822">
          <ScrollReveal delay={0.2} data-id="element-1823">
            <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-1824">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1825" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-1826">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1827">
          <ScrollReveal data-id="element-1828">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1829">
              Let's Map Out Your System
            </h2>
            <p className="text-xl text-gray-400 mb-10" data-id="element-1830">
              Book a technical scoping call. We'll review your current software
              stack and identify exactly where automation can save you time and
              money.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-1831">
              Book a Scoping Call
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}